/**
 * Gradient construction for Huesswork. See spec §9.4.
 * Interpolation happens in OKLab; the visible gradient is sampled from at
 * least 10 stops per segment, also in OKLab (not CSS's default interpolation).
 */
import { isInSrgbGamut, rgb8ToUnit, unitToRgb8 } from "./srgb.js";
import { lerpOklab, oklabToOklch, oklabToSrgbUnit, srgbUnitToOklab, } from "./oklab.js";
import { deltaE2000, rgbUnitToLab } from "./lab.js";
export const DEFAULT_GRADIENT_CONSTRAINTS = {
    // Widened from 70–150 (2026-10 feedback, see apps/server/src/huesswork/config.ts).
    minHueDiff: 100,
    maxHueDiff: 170,
    minMidChroma: 0.05,
    minMidToEndDeltaE: 15,
    minMidToPrevMidsDeltaE: 15,
    maxAttempts: 600,
};
/** Interpolate the OKLab gradient between two sRGB-8 endpoints at parameter t in [0,1]. */
export function gradientPointAt(endpoints, t) {
    const labA = srgbUnitToOklab(rgb8ToUnit(endpoints.a));
    const labB = srgbUnitToOklab(rgb8ToUnit(endpoints.b));
    const mixed = lerpOklab(labA, labB, t);
    return unitToRgb8(clampToGamut(oklabToSrgbUnit(mixed)));
}
/** At least `stopsPerSegment` evenly spaced OKLab samples from a to b, for rendering a CSS gradient. */
export function buildDisplayStops(endpoints, stopsPerSegment = 10) {
    const stops = [];
    for (let i = 0; i < stopsPerSegment; i++) {
        const t = i / (stopsPerSegment - 1);
        stops.push(gradientPointAt(endpoints, t));
    }
    return stops;
}
function clampToGamut(c) {
    return { r: clamp01(c.r), g: clamp01(c.g), b: clamp01(c.b) };
}
function clamp01(x) {
    return Math.min(1, Math.max(0, x));
}
function hueDiffDeg(h1, h2) {
    const diff = Math.abs(h1 - h2) % 360;
    return diff > 180 ? 360 - diff : diff;
}
/**
 * Draws a random OKLCH color within L [0.5,0.85], C [0.08,0.24], any hue,
 * in-gamut. Chroma range widened from the original [0.08,0.16] (2026-10) —
 * at the old, narrower range most generated pairs came out as muted
 * pastels that read as "shades of the same washed-out hue" regardless of
 * the actual hue angle; the wider range lets clearly saturated colors (a
 * real red vs. a real green, not just two pale blues) show up too. Gamut
 * rejection in buildGradient's retry loop still filters out anything that
 * clips.
 */
function randomEndpointOklab(rng) {
    const L = 0.5 + rng.next() * 0.35;
    const C = 0.08 + rng.next() * 0.16;
    const H = rng.next() * 360;
    const hRad = (H * Math.PI) / 180;
    return { L, a: C * Math.cos(hRad), b: C * Math.sin(hRad) };
}
/**
 * Attempts to build a valid gradient per spec §9.4, retrying up to
 * `constraints.maxAttempts` times. Returns null if no valid gradient was
 * found (caller should surface a friendly error, analogous to POOL_TOO_SMALL).
 */
export function buildGradient(rng, previousMiddles, constraints = DEFAULT_GRADIENT_CONSTRAINTS) {
    for (let attempt = 0; attempt < constraints.maxAttempts; attempt++) {
        const labA = randomEndpointOklab(rng);
        const labB = randomEndpointOklab(rng);
        const rgbA = oklabToSrgbUnit(labA);
        const rgbB = oklabToSrgbUnit(labB);
        if (!isInSrgbGamut(rgbA) || !isInSrgbGamut(rgbB))
            continue;
        // From here on, validate against the 8-bit quantized colors actually
        // shown to the player (quantization can shift a borderline value by a
        // fraction of a degree/ΔE, so checking the pre-quantization values could
        // let a gradient through that violates a constraint once rounded).
        const a8 = unitToRgb8(rgbA);
        const b8 = unitToRgb8(rgbB);
        const labA8 = srgbUnitToOklab(rgb8ToUnit(a8));
        const labB8 = srgbUnitToOklab(rgb8ToUnit(b8));
        const hueDiff = hueDiffDeg(oklabToOklch(labA8).H, oklabToOklch(labB8).H);
        if (hueDiff < constraints.minHueDiff || hueDiff > constraints.maxHueDiff)
            continue;
        const midLabUnclamped = lerpOklab(labA8, labB8, 0.5);
        if (!isInSrgbGamut(oklabToSrgbUnit(midLabUnclamped)))
            continue;
        const midChroma = oklabToOklch(midLabUnclamped).C;
        if (midChroma < constraints.minMidChroma)
            continue;
        // Re-derive the midpoint from the quantized endpoints via the same curve
        // the UI renders, so the guessed target always matches what
        // gradientPointAt(..., 0.5) produces for this gradient's stored a/b.
        const mid8 = gradientPointAt({ a: a8, b: b8 }, 0.5);
        const midLabCie = rgbUnitToLab(rgb8ToUnit(mid8));
        const deltaToA = deltaE2000(midLabCie, rgbUnitToLab(rgb8ToUnit(a8)));
        const deltaToB = deltaE2000(midLabCie, rgbUnitToLab(rgb8ToUnit(b8)));
        if (deltaToA < constraints.minMidToEndDeltaE || deltaToB < constraints.minMidToEndDeltaE) {
            continue;
        }
        const tooCloseToPrevious = previousMiddles.some((prev) => deltaE2000(midLabCie, rgbUnitToLab(rgb8ToUnit(prev))) < constraints.minMidToPrevMidsDeltaE);
        if (tooCloseToPrevious)
            continue;
        return { a: a8, b: b8, middle: mid8 };
    }
    return null;
}
//# sourceMappingURL=gradient.js.map